import React, { useState, useEffect, useContext } from 'react';
import {
  getDocs,
  query,
  where,
  collection,
  doc,
  updateDoc,
  orderBy,
  getDoc
} from 'firebase/firestore';
import { Card, Button, Collapse, Badge, Modal, Row, Col } from 'react-bootstrap';
import {
  BedDouble,
  CalendarDays,
  CloudMoon,
  Users,
  ChevronDown,
  ChevronUp,
  IndianRupee,
  CircleCheckBig,
  CircleX,
  CalendarCheck2,
  BanknoteX,
  Bed,
  Info,
  InfoIcon
} from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { db } from '../FireBase';
import '../css/OrderDetail.css';
import Lottie from 'lottie-react';
import noBookings from '../assets/noBookings.json';
import { Link } from 'react-router-dom';
import toast from "react-hot-toast";


const RoomBookingList = () => {
  const { currentUser } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [cancellationPolicy, setCancellationPolicy] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    fetchRoomBookings();
    fetchCancellationPolicy();
  }, [currentUser]);

  const fetchRoomBookings = async () => {
    if (!currentUser) return;
    try {
      const q = query(
        collection(db, 'room_bookings'),
        where('userId', '==', currentUser.cusmid),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      const bookings = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setOrders(bookings);
    } catch (err) {
      console.error('Error fetching bookings:', err);
    }
  };

  const fetchCancellationPolicy = async () => {
    try {
      const ref = doc(db, 'settings', 'cancellation');
      const snap = await getDoc(ref);
      if (snap.exists()) setCancellationPolicy(snap.data());
    } catch (err) {
      console.error('Error fetching cancellation settings:', err);
    }
  };

  const handleCancel = async () => {
    if (!selectedOrder) return;
    setIsCancelling(true);
    try {
      const ref = doc(db, 'room_bookings', selectedOrder.id);

      const refundPercent = getRefundPercentage(selectedOrder);
      const refundAmount = ((selectedOrder.cost || 0) * refundPercent) / 100;

      await updateDoc(ref, {
        status: 'cancelled',
        refundPercent,
        refundAmount,
      });

      setOrders(prev =>
        prev.map(o =>
          o.id === selectedOrder.id
            ? { ...o, status: 'cancelled', refundPercent, refundAmount }
            : o
        )
      );
      await fetch(
      "https://us-central1-kabili.cloudfunctions.net/api/send-cancellation-email",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderId: selectedOrder.orderId,
          refundAmount,
          refundPercent,
        }),
      }
    );
      toast.success(`Booking cancelled. Refund: ₹${refundAmount} (${refundPercent}%)`);
    } catch (err) {
      toast.error('Error cancelling booking. Please try again.');
    } finally {
      setShowConfirmModal(false);
      setIsCancelling(false);
    }
  };

  const toggleCollapse = index => setOpenIndex(openIndex === index ? null : index);

  const getStatusIcon = status => {
    switch (status?.toLowerCase()) {
      case 'booking-confirmed':
        return <CalendarCheck2 className="me-2 text-success" size={22} />;
      case 'cancelled':
        return <CircleX className="me-2 text-danger" size={22} />;
      case 'checked-in':
        return <CircleCheckBig className="me-2 text-info" size={22} />;
      case 'payment-pending':
        return <BanknoteX className="me-2 text-danger" size={22} />;
      default:
        return <CalendarCheck2 className="me-2 text-warning" size={22} />;
    }
  };

  // 🔹 Compute applicable refund percentage
  const getRefundPercentage = (order) => {
  if (!order || !order.checkIn) return 0;
  if (!cancellationPolicy?.rooms?.enabled) return 0;

  const rules = cancellationPolicy.rooms.rules || [];
  if (rules.length === 0) return 0;

  // Convert "DD/MM/YYYY" to "YYYY-MM-DD"
  const formatDate = (ddmmyyyy) => {
    const [day, month, year] = ddmmyyyy.split("/");
    return `${year}-${month}-${day}`;
  };

  const checkInDate = new Date(formatDate(order.checkIn));
  const today = new Date();

  // Convert today to YYYY-MM-DD (remove time for accurate diff)
  const todayNormalized = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const diffDays = Math.ceil(
    (checkInDate.getTime() - todayNormalized.getTime()) / (1000 * 60 * 60 * 24)
  );

  // Sort rules descending
  const sortedRules = [...rules].sort((a, b) => b.days - a.days);

  // Find rule matching diffDays
  const matchingRule = sortedRules.find(rule => diffDays >= rule.days);

  if (matchingRule) return Number(matchingRule.refund);

  // If no rule matches, return the last rule's refund
  const lastRule = sortedRules[sortedRules.length - 1];
  return Number(lastRule.refund);
};



  if (orders.length === 0) {
    return (
      <div
        className="empty-order-container"
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <div className="lottie-wrapper mb-3" style={{ width: 250 }}>
          <Lottie animationData={noBookings} loop={true} />
        </div>
        <p className="empty-order-text">No room bookings yet!</p>
      </div>
    );
  }

  return (
    <div>
      

      {orders.map((order, index) => {
        const refundPercent = getRefundPercentage(order);

        return (
          <Card key={order.id} className="mb-4 border-0 shadow-sm rounded-4">
            <Card.Header className="bg-light d-flex justify-content-between align-items-center rounded-top-4">
              <div>
                <h5 className="mb-0 d-flex align-items-center" style={{ color: '#54b435' }}>
                  <BedDouble className="me-2" /> {order.roomName}
                </h5>
                <small className="text-muted">
                  <CalendarDays size={16} className="me-1" />
                  Booking ID: {order.orderId}
                </small>
              </div>
              {getStatusIcon(order.status)}
            </Card.Header>

            <Card.Body>
              <Row>
                <Col md={6}>
                  <p><CalendarDays className="me-2 text-primary" /> Check-in: <strong>{order.checkIn}</strong></p>
                  <p><CalendarDays className="me-2 text-danger" /> Check-out: <strong>{order.checkOut}</strong></p>
                  <p><CloudMoon className="me-2 text-info" /> Nights: <strong>{order.stayDays}</strong></p>
                  <p><Bed className="me-2 text-success" /> Room count: <strong>{order.roomCount}</strong></p>
                </Col>
                <Col md={6}>
                  <p><Users className="me-2" /> Guests: {order.adultCount + order.childCount || 0} (Adult: {order.adultCount || 0}, Child: {order.childCount || 0})</p>
                  <p><IndianRupee className="me-2" /> Total: ₹{order.cost}</p>
                </Col>
              </Row>

              <Button
                size="sm"
                className="mt-3 btn-own-lite"
                varient
                onClick={() => toggleCollapse(index)}
              >
                {openIndex === index ? <ChevronUp className="me-1" /> : <ChevronDown className="me-1" />}
                {openIndex === index ? 'Hide Details' : 'View Details'}
              </Button>
         
               
              
              <Collapse in={openIndex === index}>
                <div className="mt-4">
                  <h6>Guest Details</h6>
                  <ul>
                    {order.guests?.map((guest, idx) => (
                      <li key={idx}>{guest.name} ({guest.phone})</li>
                    ))}
                  </ul>

                  <hr />
                  <h6>Cost Breakdown</h6>
                  <p>Payment method: {order.paymentMethod}</p>
                  <p>Room(Per day): ₹{order.perNightcost} x {order.roomCount}</p>
                  <p>Discount: ₹{order.discount}</p>
                  <p><strong>Total: ₹{order.cost}</strong></p>

                  <p>
                    <strong>Status: </strong>
                    <Badge
                      bg={
                        order.status === 'booking-confirmed' ? 'success' :
                        order.status === 'payment-pending' ? 'warning' :
                        order.status === 'cancelled' ? 'danger' :
                        order.status === 'checked-in' ? 'secondary' :
                        'dark'
                      }
                    >
                      {order.status}
                    </Badge>
                  </p>
                  {/* {order.status === 'booking-confirmed' && ( */}
                    <Button
                      className="w-100 mt-2 btn-own"
                      onClick={async () => {
                       const toastId = toast.loading("Sending invoice...");

                      let data = {};
                      try {
                        const res = await fetch("https://us-central1-kabili.cloudfunctions.net/api/send-invoice-copy", {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ orderId: order.orderId }),
                        });

                        try {
                          data = await res.json();
                        } catch {
                          console.warn("Response not JSON");
                        }

                        toast.dismiss(toastId);

                        if (data.success) toast.success("Invoice emailed to you!");
                        else toast.error(data.error || "Failed to send invoice");
                      } catch (err) {
                        toast.dismiss();
                        toast.error("Network error. Try again.");
                      }

                      }}
                    >
                      📩 Send Invoice to Email
                    </Button>
                   {/* )}  */}

              {/* ⭐ CANCELLED SUMMARY BLOCK */}
                            {order.status === "cancelled" && (
                              <div className="mt-3 p-3 " style={{backgroundColor:'#ffe6e6',borderRadius:'15px'}}>
                                <h6 className="text-danger">Cancellation Details</h6>
            
                                <p className="mb-1">
                                  <strong>Refund Percentage:</strong> {order.refundPercent || 0}%
                                </p>
            
                                <p className="mb-1">
                                  <strong>Refund Amount:</strong> ₹{order.refundAmount || 0}
                                </p>
                                <p style={{fontSize:'12px'}}><InfoIcon size={15} color="red"/> Refund will be initiated in 3 business days. For any refund-related queries, please contact us.</p>
                                
            
                                
                              </div>
                            )}
                  {/* 🔹 Cancellation Section */}
                  {order.status !== 'cancelled' && cancellationPolicy?.rooms?.enabled && (
                    <>
                      <div className="d-flex flex-column align-items-center mt-3">
                      

                        <p className="text-muted mb-2">
                          {refundPercent > 0
                            ? `If you cancel now, you will receive a ${refundPercent}% refund.`
                            : `Cancellation not refundable.`}
                        </p>

                        <Button
                          className="w-100 mt-1 btn-own-danger"
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowConfirmModal(true);
                          }}
                        >
                          Cancel Booking
                        </Button>
                        <br/>
                          <Link to="/cancellation-policy" className="text-decoration-none text-primary mb-2" >
                          <Info className="me-1" size={16} /> Read Cancellation Policy
                        </Link>
                      </div>
                    </>
                  )}

                  {order.status !== 'cancelled' && !cancellationPolicy?.rooms?.enabled && (
                    <p className="text-muted mt-3">
                      <em>Cancellation not available for this booking.</em>
                    </p>
                  )}
                </div>
               

              </Collapse>
            </Card.Body>
          </Card>
        );
      })}

      {/* 🔹 Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Cancellation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to cancel this booking? <br />
          <strong>Refund:</strong> {selectedOrder ? getRefundPercentage(selectedOrder) : 0}%
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            No
          </Button>
          <Button variant="danger" onClick={handleCancel} disabled={isCancelling}>
               {isCancelling ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Cancelling...
                  </>
                ) : (
                  "Yes, Cancel"
                )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RoomBookingList;
